// @flow
import React from 'react';
import { createUseStyles } from 'react-jss';
import _ from 'shared/lib/gettext';
import cx from 'classnames';
import { gql, useQuery } from '@apollo/client';
import Select from '~/app/components/Select';
import Section from './Section';
import SettingRow from './SettingRow';
import TemplateEditor from './TemplateEditor';
import { getTemplateSettingsSchema } from 'shared/templates/util';
import getPathWithTrailingSlash from 'shared/lib/getPathWithTrailingSlash';
import { S, labelStyle } from './shell/shellTokens';

type Data = {
  metadata: string,
  title: string,
  path: string,
  pageTemplate: Object
};
type Props = {
  classes: Object,
  /** The inspector stays mounted while closed, so open sections and typing survive. */
  open: boolean,
  onClose: () => void,
  onChangeVersion: string => void,
  onTemplateSettingsChange: () => void,
  onChangePageSettings: () => void,
  onChangeCategory: () => void,
  categories: Array<Object>,
  data: Data,
  template: Object,
  isFetching: boolean
};
type State = {
  openSections: $FlowTODO,
  openTemplateSettings: boolean,
  draft: Data
}

// Helper function to transform cxc10 display to AIXC
const transformCxc10ToAixc = (path) => {
  if (!path) return path;
  return path.replace(/\/cxc10/g, '/aixc');
};

// Helper function to transform aixc back to cxc10 for actual path
const transformAixcToCxc10 = (path) => {
  if (!path) return path;
  return path.replace(/\/aixc/g, '/cxc10');
};

const SidebarContainer = (props) => {
  const { data } = props;

  // getTemplateAndCategories
  const { data: templateData } = useQuery(QUERY, {
    variables: { id: data.pageTemplate.templateId }
  });
  const template = templateData?.template;
  const categories = templateData?.categories;

  const classes = useStyles();

  return <Sidebar
    {...props}
    {...{
      template,
      categories,
      classes
    }}
  />;
}

class Sidebar extends React.Component {
  props: Props = this.props;

  state: State = {
    openTemplateSettings: false,
    openSections: { PAGE: true },
  };

  handleClickSettingTitle = (section) => {
    const isOpen = !!this.state.openSections[section];

    this.setState({
      openSections: {
        ...this.state.openSections,
        [section]: !isOpen
      }
    });
  }

  SEOSettings = ({ c, data }) => (
    <Section
      title="SEO"
      onClick={() => this.handleClickSettingTitle('SEO')}
      isOpen={this.state.openSections.SEO}
    >
      <div className={c.sectionSettings}>
        {data.metadata.map(meta =>
          <SettingRow
            key={`meta-${meta.key}`}
            label={meta.key}
            id={`meta-${meta.key}`}
            value={meta.value}
            onChange={(id, value) => this.props.onChangePageSettings(id, value)}
          />
        )}
      </div>
    </Section>
  );

  PageSettings = ({ c, data }) => {
    const {
      openSections: {
        PAGE: isOpen
      }
    } = this.state;
    const {
      onChangePageSettings,
      onChangeCategory,
      categories
    } = this.props;

    const CategorySetting = () => (
      <div className={c.selectSetting}>
        <label htmlFor="temp-category" className={cx(c.sectionSettingLabel, c.pageSettingsLabel)}>
          {_('CATEGORY')}
        </label>
        <Select
          {...LIGHT_SELECT}
          options={categories.map(cat => transformCxc10ToAixc(cat.path))}
          selected={transformCxc10ToAixc(data.category)}
          onSelect={value => {
            // Convert the display path back to actual path before finding the category
            const actualPath = transformAixcToCxc10(value);
            onChangeCategory(categories.find(cat => cat.path === actualPath));
          }}
        />
      </div>
    );

    const pathWithTrailingSlash = getPathWithTrailingSlash(data.path);
    // Transform cxc10 to aixc for display purposes
    const displayPathWithTrailingSlash = transformCxc10ToAixc(pathWithTrailingSlash);

    return (
      <Section
        title={_('PAGE')}
        onClick={() => this.handleClickSettingTitle('PAGE')}
        isOpen={isOpen}
      >
        <div className={c.sectionSettings}>
          <SettingRow
            label={_('TITLE')}
            id="title"
            value={data.title}
            onChange={(id, value) => onChangePageSettings(id, value)}
          />
          {categories && <CategorySetting />}
          <SettingRow
            label={_('PATH_WITH_NOTE')}
            id="path"
            value={data.path.split('/').slice(-1)}
            onChange={(id, value) => onChangePageSettings(id, value)}
          />
          <SettingRow
            label={_('CANONICAL_RELATIONSHIP')}
            id="canonicalRel"
            value={data.canonicalRel}
            placeholder={displayPathWithTrailingSlash}
            title={`default to ${displayPathWithTrailingSlash}`}
            onChange={(id, value) => onChangePageSettings(id, value)}
          />
        </div>
      </Section>
    );
  };

  render() {
    const { classes: c, data, template, onTemplateSettingsChange, open, onClose } = this.props;
    const hasTemplateSettings = getTemplateSettingsSchema(data.pageTemplate.templateId).length > 0;
    const TemplateSettings = () => (
      <Section
        title={_('TEMPLATE')}
        onClick={() => this.handleClickSettingTitle('TEMPLATE')}
        isOpen={this.state.openSections.TEMPLATE}
      >
        <div className={c.sectionSettings}>
          <div className={c.sectionSetting}>
            <label htmlFor="temp-version" className={c.sectionSettingLabel}>
              {_('TEMPLATE_VERSION')}
            </label>
            <Select
              {...LIGHT_SELECT}
              options={this.props.template.versions}
              selected={data.pageTemplate.version}
              onSelect={this.props.onChangeVersion}
            />
          </div>
          <div className={c.sectionSetting}>
            {/* Not a <label for> the button: that would rename the button "Template Settings" for screen readers and AI assistants. */}
            <span className={c.sectionSettingLabel}>
              {_('TMEPLATE_SETTINGS')}
            </span>
            {hasTemplateSettings ? (
              <button
                type="button"
                id="temp-settings"
                onClick={() => this.setState({ openTemplateSettings: true })}
                className={c.secondaryButton}
              >{_('EDIT_TEMPLATE_SETTINGS')}</button>
            ) : (
              <span className={c.noTemplateSettings}>{_('NO_TEMPLATE_SETTINGS')}</span>
            )}
          </div>
        </div>
      </Section>
    );
    const templateEditor = this.state.openTemplateSettings && (
      <TemplateEditor
        template={data.pageTemplate} onClose={() => this.setState({
          openTemplateSettings: false
        })}
        onSave={onTemplateSettingsChange}
      />
    );

    return (
      <aside className={cx(c.inspector, !open && c.closed)} aria-label="Page settings" data-testid="page-inspector" data-toast-beside>
        <div className={c.head}>
          <span className={c.title}>Page settings</span>
          <button type="button" className={c.close} onClick={onClose} aria-label="Close page settings">
            <i className="material-icons">close</i>
          </button>
        </div>
        <this.PageSettings data={data} c={c} />
        <this.SEOSettings data={data} c={c} />
        {template && <TemplateSettings />}
        {templateEditor}
      </aside>
    );
  }
}

const LIGHT_SELECT = {
  bgColor: S.field,
  color: S.fg,
  selectedBackground: S.line,
  hoverColor: S.fg,
  hoverBackground: S.chromeHover,
  fontSize: '13px'
};

const styles = {
  inspector: {
    width: 300,
    flexShrink: 0,
    overflowY: 'auto',
    boxSizing: 'border-box',
    padding: '0 16px 24px',
    background: S.chrome,
    borderLeft: `1px solid ${S.line}`,
    color: S.fg,
    fontFamily: S.ui,
    fontSize: 13
  },
  closed: { display: 'none' },
  head: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 0 10px',
    background: S.chrome
  },
  title: { ...labelStyle, color: S.fg },
  close: {
    all: 'unset',
    cursor: 'pointer',
    width: 28,
    height: 28,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
    color: S.muted,
    '& .material-icons': { fontSize: 18 },
    '&:hover, &:focus-visible': { background: S.chromeHover, color: S.fg }
  },
  sectionSettings: {
    display: 'none',
    paddingBottom: 8,
    '.is-open > &': {
      display: 'block'
    }
  },
  sectionSetting: {
    marginBottom: 12
  },
  sectionSettingLabel: {
    display: 'block',
    marginBottom: 5,
    fontSize: 12,
    color: S.muted
  },
  selectSetting: {
    marginBottom: 12
  },
  pageSettingsLabel: {
    fontSize: 12
  },
  noTemplateSettings: {
    fontSize: 13,
    color: S.muted
  },
  secondaryButton: {
    all: 'unset',
    boxSizing: 'border-box',
    cursor: 'pointer',
    display: 'block',
    width: '100%',
    textAlign: 'center',
    padding: '7px 12px',
    fontSize: 13,
    fontWeight: 600,
    borderRadius: 6,
    border: `1px solid ${S.fieldLine}`,
    background: S.field,
    color: S.fg,
    '&:hover, &:focus-visible': { borderColor: S.fg }
  }
};

const useStyles = createUseStyles(styles);

const QUERY = gql`
query getTemplateAndCategories($id: ID!) {
  template(id: $id) {
    versions
    latestVersion
  }
  categories {
    _id
    path
    title
  }
}
`;

export default SidebarContainer;
